import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, Trash2, X, CreditCard, Sparkles, GitBranch, UserPlus, UserMinus, AtSign, MessageSquare, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { notificationsApi } from '../../services/api';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

const NOTIFICATION_ICONS = {
  subscription_alert: CreditCard,
  ai_complete: Sparkles,
  kanban_update: GitBranch,
  feature_assigned: UserPlus,
  feature_unassigned: UserMinus,
  mention: AtSign,
  new_note: MessageSquare,
  kpi_due_reminder: Clock,
};

const PRIORITY_COLORS = {
  high: 'bg-red-500',
  medium: 'bg-yellow-500',
  normal: 'bg-blue-500',
};

const NotificationBell = ({ showLabel = false }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const navigate = useNavigate();
  
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  
  // Fetch unread count on mount and periodically
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000); // Poll every 30s
    return () => clearInterval(interval);
  }, []);
  
  // Fetch notifications when panel opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);
  
  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  
  const fetchUnreadCount = async () => {
    try {
      const res = await notificationsApi.getUnreadCount();
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
    }
  };
  
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await notificationsApi.list({ limit: 20 });
      setNotifications(res.data.notifications || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationsApi.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };
  
  const handleMarkAllAsRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };
  
  const handleDeleteAll = async () => {
    try {
      await notificationsApi.clearAll();
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to delete all notifications:', err);
    }
  };
  
  const handleDelete = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await notificationsApi.delete(notificationId);
      setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
      // Update unread count if deleted notification was unread
      const deleted = notifications.find(n => n.notification_id === notificationId);
      if (deleted && !deleted.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };
  
  const handleNotificationClick = async (notification) => {
    // Delete the notification on click (dismiss behavior)
    try {
      await notificationsApi.delete(notification.notification_id);
      setNotifications(prev => prev.filter(n => n.notification_id !== notification.notification_id));
      if (!notification.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to dismiss notification:', err);
    }
    
    // Handle KPI due reminder - navigate to product and open goal modal
    if (notification.notification_type === 'kpi_due_reminder' && notification.metadata?.product_id) {
      const { product_id, goal_id } = notification.metadata;
      // Navigate to product page with query params to open goal modal
      navigate(`/products/${product_id}?openGoal=${goal_id}&tab=kpis`);
      setIsOpen(false);
      return;
    }
    
    // Navigate if link exists
    if (notification.link) {
      navigate(notification.link);
      setIsOpen(false);
    }
  };
  
  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return '';
    }
  };
  
  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "relative p-2 flex items-center gap-3",
          showLabel ? "w-full justify-start px-3 py-2" : "",
          colors.textMuted, 
          "hover:bg-white/10"
        )}
      >
        <div className="relative">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </div>
        {showLabel && (
          <span className={cn("text-sm", colors.textMuted)}>Notifications</span>
        )}
      </Button>
      
      {/* Notification Panel - opens upward since bell is at bottom of sidebar */}
      {isOpen && (
        <Card className={cn(
          "absolute left-full bottom-0 ml-2 w-96 max-h-[80vh] overflow-hidden z-50 shadow-xl flex flex-col",
          colors.bgCard, colors.borderPrimary, "border"
        )}>
          {/* Header */}
          <div className={cn("flex items-center justify-between p-4 border-b flex-shrink-0", colors.borderPrimary)}>
            <h3 className={cn("font-semibold flex items-center gap-2", colors.textPrimary)}>
              <Bell className="w-4 h-4" />
              Notifications
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllAsRead}
                  className={cn("text-xs", colors.textMuted, "hover:text-white")}
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
              ) : notifications.length > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteAll}
                  className={cn("text-xs text-red-400 hover:text-red-300")}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete all
                </Button>
              ) : null}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className={cn(colors.textMuted, "hover:text-white p-1")}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          {/* Notification List */}
          <div className="overflow-y-auto flex-1 min-h-0">
            {loading ? (
              <div className={cn("p-8 text-center", colors.textMuted)}>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div className={cn("p-8 text-center", colors.textMuted)}>
                <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
                const priorityColor = PRIORITY_COLORS[notification.priority] || PRIORITY_COLORS.normal;
                
                return (
                  <div
                    key={notification.notification_id}
                    onClick={() => handleNotificationClick(notification)}
                    className={cn(
                      "p-4 border-b cursor-pointer transition-colors",
                      colors.borderPrimary,
                      notification.read ? 'opacity-60' : '',
                      "hover:bg-white/5"
                    )}
                  >
                    <div className="flex gap-3">
                      {/* Icon with priority indicator */}
                      <div className="relative flex-shrink-0">
                        <div className={cn(
                          "w-10 h-10 rounded-full flex items-center justify-center",
                          notification.read ? 'bg-white/5' : 'bg-white/10'
                        )}>
                          <Icon className={cn("w-5 h-5", colors.textSecondary)} />
                        </div>
                        {!notification.read && (
                          <div className={cn("absolute -top-1 -right-1 w-3 h-3 rounded-full", priorityColor)} />
                        )}
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={cn("font-medium text-sm", colors.textPrimary)}>
                          {notification.title}
                        </p>
                        <p className={cn("text-sm mt-0.5 line-clamp-2", colors.textMuted)}>
                          {notification.message}
                        </p>
                        <p className={cn("text-xs mt-1", colors.textMuted)}>
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                      
                    </div>
                  </div>
                );
              })
            )}
          </div>
          
          {/* Footer - View All / Settings */}
          {notifications.length > 0 && (
            <div className={cn("p-3 border-t flex justify-center", colors.borderPrimary)}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  navigate('/settings?tab=notifications');
                  setIsOpen(false);
                }}
                className={cn("text-xs", colors.textMuted, "hover:text-white")}
              >
                Notification Settings
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default NotificationBell;
